import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Send, CheckCircle2, Sparkles, AlertCircle, Trash2 } from 'lucide-react';
import StatusBadge from '../components/shared/StatusBadge';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { bcIcon } from '../lib/businessCategories';
import GroupActionsMenu from '../components/broadcast/GroupActionsMenu';

// Slugify a Business Category name for stable data-testid values.
// "Men's Wear" → "men-s-wear"
const bcSlug = (bc) => (bc || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

// Feb 2026 UI Cleanup — the following seller-facing sections are hidden per
// the "Catalog Broadcast UI Cleanup" spec. Backend + data + APIs are
// UNTOUCHED — flip this flag back to `true` to restore the full UI.
const SHOW_LEGACY_BROADCAST_UI = false;

export default function Broadcast() {
  const { seller } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const preselectIds = location.state?.preselectProductIds || null;
  const [products, setProducts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [openCollection, setOpenCollection] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [suggestedGroups, setSuggestedGroups] = useState([]);
  const [selProd, setSelProd] = useState([]);
  const [selGrp, setSelGrp] = useState([]);
  const [connection, setConnection] = useState(null);
  // Catalogue Broadcast — multi-select + delete (Jun 2026)
  const [selColl, setSelColl] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const toggleColl = (id) => setSelColl((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const confirmDeleteCollections = async () => {
    if (selColl.length === 0) return;
    setDeleting(true);
    try {
      const { data } = await api.post('/collections/bulk-delete', { ids: selColl });
      toast.success(`${data.deleted} catalogue${data.deleted === 1 ? '' : 's'} deleted`);
      setSelColl([]);
      setShowDeleteConfirm(false);
      load();
    } catch (e) {
      toast.error('Failed to delete catalogues. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const load = () => {
    api.get('/products?limit=200').then(({ data }) => setProducts(data.items));
    api.get('/whatsapp/groups').then(({ data }) => setGroups(data.items || []));
    api.get('/broadcasts').then(({ data }) => setBroadcasts(data.items));
    api.get('/collections').then(({ data }) => setCollections(data.items || [])).catch(() => setCollections([]));
    // Feb 2026: filter/chips are driven by the seller's Business Categories,
    // never expanded into master-category sub-types. `categories` now holds
    // an array of BC name strings, e.g. ["Fabric","Ladies Wear",...].
    setCategories(seller?.business_categories || []);
    api.get('/whatsapp/connection-status').then(({ data }) => setConnection(data)).catch(() => setConnection(null));
  };
  useEffect(() => { load(); }, [seller]);

  // Handoff from ProductCreate → Broadcast: preselect the freshly-created
  // products and jump the scroll to the top. Clear the router state so a
  // browser back/forward doesn't re-apply the preselection.
  useEffect(() => {
    if (preselectIds && preselectIds.length) {
      setSelProd(preselectIds);
      toast.success(`${preselectIds.length} product${preselectIds.length === 1 ? '' : 's'} preselected. Now pick your broadcast groups.`);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []);

  // Refresh AI group suggestions when category changes.
  // `selectedCategory` now holds a Business Category NAME string (e.g.
  // "Ladies Wear") — pass it through as `category_name` so the AI can still
  // reason about the seller-facing category label.
  useEffect(() => {
    if (!selectedCategory) { setSuggestedGroups([]); return; }
    api.get(`/broadcasts/suggest-groups?category_id=${encodeURIComponent(selectedCategory)}&category_name=${encodeURIComponent(selectedCategory)}`)
      .then(({ data }) => {
        const suggIds = (data.suggestions || []).map(s => s.id);
        setSuggestedGroups(data.suggestions || []);
        // Auto pre-select top suggestions (max 5)
        if (suggIds.length > 0) {
          setSelGrp((prev) => Array.from(new Set([...prev, ...suggIds.slice(0, 5)])));
        }
      })
      .catch(() => setSuggestedGroups([]));
  }, [selectedCategory, categories]);

  const filteredProducts = useMemo(() => {
    if (!selectedCategory) return products;
    // Products carry `business_category` since Feb 2026. Match against the
    // BC name string held in `selectedCategory`.
    return products.filter(p => (p.business_category || '') === selectedCategory);
  }, [products, selectedCategory]);

  const sortedGroups = useMemo(() => {
    if (suggestedGroups.length === 0) return groups;
    const scoreById = new Map(suggestedGroups.map(s => [s.id, s.score]));
    return [...groups].sort((a, b) => (scoreById.get(b.id) || 0) - (scoreById.get(a.id) || 0));
  }, [groups, suggestedGroups]);

  const sendNow = async () => {
    if (selProd.length === 0 || selGrp.length === 0) { toast.error('Select products and groups'); return; }
    await api.post('/broadcasts', { product_ids: selProd, group_ids: selGrp });
    toast.success(`Broadcasting ${selProd.length} products to ${selGrp.length} groups...`);
    setSelProd([]); setSelGrp([]);
    setTimeout(load, 4000);
  };

  const toggle = (arr, set, id) => set(arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id]);

  return (
    <div className="space-y-5 fade-up">
      {SHOW_LEGACY_BROADCAST_UI && connection && !connection.broadcast_ready && (
        <div className="fab-card p-4 flex flex-col sm:flex-row sm:items-start gap-3 border-l-4 border-fab-warning bg-amber-50" data-testid="broadcast-not-ready">
          <div className="flex items-start gap-3 flex-1">
            <AlertCircle className="w-5 h-5 text-fab-warning shrink-0 mt-0.5" />
            <div className="text-[13px] text-fab-text flex-1">
              <div className="font-semibold">WhatsApp not ready for broadcast</div>
              <div className="text-fab-text-secondary mt-0.5">{connection.reason}</div>
            </div>
          </div>
          <Link to="/dashboard/whatsapp" className="px-3 py-2 rounded-lg bg-fab-warning text-white text-[12px] font-semibold whitespace-nowrap text-center w-full sm:w-auto shrink-0">
            Open WhatsApp settings
          </Link>
        </div>
      )}
      <div className={SHOW_LEGACY_BROADCAST_UI ? 'grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4' : ''}>
        {SHOW_LEGACY_BROADCAST_UI && <div className="fab-card p-3 sm:p-4 text-center"><div className="text-[10px] sm:text-[11px] text-fab-text-muted uppercase tracking-wider">Selected products</div><div className="font-display text-xl sm:text-2xl font-bold text-fab-primary">{selProd.length}</div></div>}
        {SHOW_LEGACY_BROADCAST_UI && <div className="fab-card p-3 sm:p-4 text-center"><div className="text-[10px] sm:text-[11px] text-fab-text-muted uppercase tracking-wider">Selected groups</div><div className="font-display text-xl sm:text-2xl font-bold text-fab-primary">{selGrp.length}</div></div>}
        {SHOW_LEGACY_BROADCAST_UI && <div className="fab-card p-3 sm:p-4 text-center"><div className="text-[10px] sm:text-[11px] text-fab-text-muted uppercase tracking-wider">Total broadcasts</div><div className="font-display text-xl sm:text-2xl font-bold text-fab-text">{broadcasts.length}</div></div>}
        <div className={`fab-card p-3 sm:p-4 ${SHOW_LEGACY_BROADCAST_UI ? 'col-span-2 lg:col-span-1' : ''}`}>
          <label className="text-[10px] text-fab-text-muted uppercase tracking-wider font-semibold">Filter by category</label>
          <select
            data-testid="broadcast-category-filter"
            value={selectedCategory}
            onChange={(e) => { setSelectedCategory(e.target.value); setSelProd([]); }}
            className="w-full mt-1 px-2 py-1.5 rounded-md border border-fab-primary-lighter text-[13px] focus:outline-none focus:border-fab-primary"
          >
            <option value="">All categories</option>
            {categories.map(c => <option key={c} value={c}>{bcIcon(c)} {c}</option>)}
          </select>
        </div>
      </div>

      {/* Quick category chips — one-click "Select all <Category>" */}
      {SHOW_LEGACY_BROADCAST_UI && categories.length > 0 && (
        <div className="fab-card p-3" data-testid="quick-category-strip">
          <div className="text-[10px] uppercase tracking-wider text-fab-text-muted font-semibold mb-2 px-1">Quick add by category</div>
          <div className="flex flex-wrap gap-1.5">
            {categories.map(c => {
              const catProducts = products.filter(p => (p.business_category || '') === c);
              if (catProducts.length === 0) return null;
              const catIds = catProducts.map(p => p.id);
              const allIn = catIds.every(id => selProd.includes(id));
              return (
                <button
                  key={c}
                  data-testid={`quick-cat-${bcSlug(c)}`}
                  onClick={() => {
                    if (allIn) setSelProd(selProd.filter(id => !catIds.includes(id)));
                    else setSelProd(Array.from(new Set([...selProd, ...catIds])));
                  }}
                  className={`px-3 py-1.5 rounded-full text-[12px] font-semibold flex items-center gap-1.5 transition-all ${allIn ? 'bg-fab-primary text-white' : 'bg-fab-elevated text-fab-text-secondary hover:bg-fab-primary-lighter'}`}
                  title={allIn ? `Deselect ${catProducts.length} ${c}` : `Select all ${catProducts.length} ${c}`}
                >
                  <span>{bcIcon(c)}</span>
                  {allIn ? 'Deselect ' : 'Select all '}
                  {catProducts.length} {c}
                  {allIn && <span className="text-white">✓</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {selectedCategory && suggestedGroups.length > 0 && (        <div className="fab-card p-4 flex items-start gap-3 border-l-4 border-fab-primary" data-testid="broadcast-ai-suggestion">
          <div className="w-8 h-8 rounded-lg bg-fab-primary-lighter flex items-center justify-center shrink-0">
            <Sparkles className="w-4 h-4 text-fab-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold text-fab-text">
              AI suggested {suggestedGroups.length} group{suggestedGroups.length === 1 ? '' : 's'} for this category
            </div>
            <div className="text-[12px] text-fab-text-muted mt-1">
              Top picks pre-selected. Uncheck any you don&apos;t want.
              <span className="ml-2">
                {suggestedGroups.slice(0, 4).map(s => (
                  <span key={s.id} className="inline-block mr-1 px-1.5 py-0.5 rounded bg-fab-primary-lighter text-fab-primary text-[10px] font-mono">
                    {s.group_name}
                  </span>
                ))}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Saved Collections — Feb 2026 Step 3 · Task 2 */}
        {collections.length > 0 && (
          <div className="fab-card p-4 sm:p-5 lg:col-span-2" data-testid="collections-section">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <div className="font-display font-bold text-fab-text">
                Your Collections <span className="text-[11px] text-fab-text-muted font-normal ml-1">({collections.length})</span>
              </div>
              {selColl.length > 0 && (
                <div className="flex items-center gap-2" data-testid="catalogue-selection-bar">
                  <span className="text-[12px] font-medium text-fab-text-secondary" data-testid="catalogue-selected-count">
                    {selColl.length} catalogue{selColl.length === 1 ? '' : 's'} selected
                  </span>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    data-testid="delete-selected-catalogues"
                    className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-[12px] font-semibold inline-flex items-center gap-1.5 hover:bg-red-700 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete Selected
                  </button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {collections.map((c) => (
                <div
                  key={c.id}
                  data-testid={`collection-card-${c.id}`}
                  className={`relative rounded-xl overflow-hidden border transition-all ${selColl.includes(c.id) ? 'border-fab-primary ring-2 ring-fab-primary' : 'border-fab-primary-lighter hover:border-fab-primary hover:shadow-md'}`}
                >
                  <label
                    className="absolute top-1.5 left-1.5 z-10 w-6 h-6 rounded-md bg-white/90 border border-fab-primary-lighter flex items-center justify-center cursor-pointer shadow-sm"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selColl.includes(c.id)}
                      onChange={() => toggleColl(c.id)}
                      className="accent-fab-primary w-4 h-4 cursor-pointer"
                      data-testid={`catalogue-select-${c.id}`}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => setOpenCollection(c.id)}
                    data-testid={`catalogue-open-${c.id}`}
                    className="block w-full text-left"
                  >
                    <div className="aspect-square bg-fab-elevated relative">
                      {c.cover_image_url ? (
                        <img src={c.cover_image_url} alt={c.name} className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-fab-text-muted">
                          <Sparkles className="w-6 h-6" />
                        </div>
                      )}
                      <div className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded-full bg-black/65 text-white text-[10px] font-bold">
                        {c.product_count} item{c.product_count === 1 ? '' : 's'}
                      </div>
                    </div>
                    <div className="p-2.5">
                      <div className="text-[12px] font-semibold text-fab-text truncate">{c.name}</div>
                      <div className="text-[10px] text-fab-text-muted">
                        {c.created_at ? new Date(c.created_at).toLocaleDateString('en-IN', { dateStyle: 'medium' }) : ''}
                      </div>
                    </div>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={`fab-card p-4 sm:p-5${SHOW_LEGACY_BROADCAST_UI ? '' : ' hidden'}`}>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="font-display font-bold text-fab-text">
              Select Products
              {selectedCategory && <span className="text-[11px] text-fab-text-muted font-normal ml-2">({filteredProducts.length} in {selectedCategory})</span>}
            </div>
            <div className="flex items-center gap-1.5">
              {selectedCategory && filteredProducts.length > 0 && (() => {
                const catIds = filteredProducts.map(p => p.id);
                const allSelected = catIds.every(id => selProd.includes(id));
                const someSelected = catIds.some(id => selProd.includes(id));
                return (
                  <>
                    <button
                      data-testid="select-all-category"
                      onClick={() => setSelProd(Array.from(new Set([...selProd, ...catIds])))}
                      disabled={allSelected}
                      className="px-2.5 py-1 rounded-md bg-fab-primary text-white text-[11px] font-semibold disabled:opacity-50 flex items-center gap-1"
                    >
                      <CheckCircle2 className="w-3 h-3" />
                      Select all {selectedCategory}
                    </button>
                    {someSelected && (
                      <button
                        data-testid="deselect-all-category"
                        onClick={() => setSelProd(selProd.filter(id => !catIds.includes(id)))}
                        className="px-2.5 py-1 rounded-md border border-fab-primary-lighter text-fab-text-secondary text-[11px] font-semibold hover:bg-fab-elevated"
                      >
                        Deselect all
                      </button>
                    )}
                  </>
                );
              })()}
              {!selectedCategory && (
                <button onClick={() => setSelProd(selProd.length === filteredProducts.length ? [] : filteredProducts.map(p => p.id))} className="text-[11px] text-fab-primary font-semibold" data-testid="select-all-everything">
                  {selProd.length === filteredProducts.length && filteredProducts.length > 0 ? 'Clear' : 'Select all visible'}
                </button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 max-h-[400px] overflow-y-auto" data-testid="broadcast-products">
            {filteredProducts.map((p) => (
              <button key={p.id} onClick={() => toggle(selProd, setSelProd, p.id)} className={`relative aspect-square rounded-lg overflow-hidden border-2 ${selProd.includes(p.id) ? 'border-fab-primary' : 'border-transparent'}`}>
                <img src={p.processed_image_url} alt={p.product_code} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute bottom-1 left-1 right-1 px-1 py-0.5 rounded bg-black/65 text-white text-[9px] font-mono text-center truncate">{p.product_code}</div>
                {selProd.includes(p.id) && <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-fab-primary flex items-center justify-center"><CheckCircle2 className="w-3 h-3 text-white" /></div>}
              </button>
            ))}
            {filteredProducts.length === 0 && <div className="col-span-3 text-center text-[12px] text-fab-text-muted py-6">No products in this category yet.</div>}
          </div>
        </div>

        <div className={`fab-card p-4 sm:p-5${SHOW_LEGACY_BROADCAST_UI ? '' : ' hidden'}`}>
          <div className="font-display font-bold text-fab-text mb-3">Select Groups / Lists</div>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {sortedGroups.length === 0 && <div className="text-[13px] text-fab-text-muted">No WhatsApp groups yet. Add some from the WhatsApp page.</div>}
            {sortedGroups.map((g) => {
              const sugg = suggestedGroups.find(s => s.id === g.id);
              const selected = selGrp.includes(g.id);
              const toggleSelection = () => toggle(selGrp, setSelGrp, g.id);
              return (
                <div
                  key={g.id}
                  role="button"
                  tabIndex={0}
                  onClick={toggleSelection}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSelection(); } }}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border ${selected ? 'bg-fab-primary-lighter border-fab-primary' : 'border-fab-primary-lighter hover:bg-fab-elevated'}`}
                  data-testid={`broadcast-group-${g.id}`}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={toggleSelection}
                    onClick={(e) => e.stopPropagation()}
                    className="accent-fab-primary"
                    data-testid={`broadcast-group-checkbox-${g.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-fab-text flex items-center gap-2">
                      <span className="truncate">{g.group_name || g.name || 'Untitled group'}</span>
                      {sugg && (
                        <span className="px-1.5 py-0.5 rounded bg-fab-primary text-white text-[9px] font-bold uppercase tracking-wider flex items-center gap-0.5">
                          <Sparkles className="w-2.5 h-2.5" /> AI
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-fab-text-muted">{g.member_count} members · {(g.type || 'RESELLER').replace('_', ' ')}</div>
                  </div>
                  <GroupActionsMenu group={g} onChanged={() => api.get('/whatsapp/groups').then(({ data }) => setGroups(data.items || []))} />
                </div>
              );
            })}
          </div>
          <button onClick={sendNow} data-testid="send-broadcast" className="mt-4 w-full btn-wa py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2"><Send className="w-4 h-4" /> Send Broadcast</button>
        </div>
      </div>

      {SHOW_LEGACY_BROADCAST_UI && (
      <div className="fab-card p-4 sm:p-5">
        <div className="font-display font-bold text-fab-text mb-3">Recent Broadcasts</div>
        <div className="space-y-2">
          {broadcasts.slice(0, 8).map((b) => {
            const total = (b.sent || 0) + (b.failed || 0);
            const firstErr = (b.send_log || []).find((s) => !s.ok);
            const dlv = b.delivery || {};
            const hasDeliveryData = (dlv.total_attempts || 0) > 0;
            const hasLegacyData = total > 0;
            return (
              <div key={b.id} className="p-3 rounded-lg hover:bg-fab-elevated" data-testid={`broadcast-row-${b.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold text-fab-text">{b.total_products} products → {b.total_groups} groups</div>
                    <div className="text-[11px] text-fab-text-muted">{new Date(b.created_at).toLocaleString()}</div>
                  </div>
                  <div className="text-right">
                    <StatusBadge status={b.status} size="sm" />
                    {hasLegacyData && !hasDeliveryData && (
                      <div className="text-[10px] mt-1 font-mono">
                        <span className="text-emerald-700">{b.sent || 0} sent</span>
                        {(b.failed || 0) > 0 && <span className="text-red-700"> · {b.failed} failed</span>}
                      </div>
                    )}
                    {!hasLegacyData && !hasDeliveryData && b.status === 'COMPLETED' && (
                      <div className="text-[10px] mt-1 text-fab-text-muted italic" data-testid={`broadcast-no-data-${b.id}`}>
                        No delivery data
                      </div>
                    )}
                  </div>
                </div>

                {hasDeliveryData && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap text-[10px] font-mono" data-testid={`broadcast-delivery-${b.id}`}>
                    <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                      Sent {dlv.accepted}
                    </span>
                    <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">
                      Delivered {dlv.delivered}
                    </span>
                    {dlv.read > 0 && (
                      <span className="px-2 py-0.5 rounded bg-purple-100 text-purple-700">
                        Read {dlv.read}
                      </span>
                    )}
                    {dlv.queued > 0 && (
                      <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800">
                        Queued {dlv.queued}
                      </span>
                    )}
                    {dlv.failed > 0 && (
                      <span className="px-2 py-0.5 rounded bg-red-100 text-red-700">
                        Failed {dlv.failed}
                      </span>
                    )}
                    <span className="text-fab-text-muted normal-case">
                      ({dlv.total_attempts} attempts · live from Meta delivery callbacks)
                    </span>
                  </div>
                )}

                {b.status === 'FAILED' && b.error && (
                  <div className="mt-2 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded p-2">
                    <strong>Error:</strong> {b.error}
                  </div>
                )}
                {(b.failed || 0) > 0 && firstErr && (
                  <details className="mt-2 text-[11px]">
                    <summary className="cursor-pointer text-fab-text-secondary hover:text-fab-text">
                      Show {b.failed} delivery failure{b.failed > 1 ? 's' : ''}
                    </summary>
                    <div className="mt-1 space-y-0.5 text-fab-text-muted bg-fab-elevated rounded p-2 max-h-40 overflow-y-auto">
                      {(b.send_log || []).filter((s) => !s.ok).map((s, i) => (
                        <div key={i} className="font-mono text-[10px]">
                          <span className="text-red-700">✗</span> <span className="font-semibold">{s.phone}</span> · {s.product_code} → {s.error}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            );
          })}
          {broadcasts.length === 0 && <div className="text-[13px] text-fab-text-muted">No broadcasts yet.</div>}
        </div>
      </div>
      )}

      {openCollection && (
        <CollectionDetailModal cid={openCollection} onClose={() => setOpenCollection(null)} />
      )}

      {showDeleteConfirm && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => !deleting && setShowDeleteConfirm(false)}
          data-testid="delete-catalogue-confirm"
        >
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div className="min-w-0">
                <div className="font-display font-bold text-fab-text text-[15px]" data-testid="delete-catalogue-title">
                  Delete {selColl.length} selected catalogue{selColl.length === 1 ? '' : 's'}?
                </div>
                <div className="text-[13px] text-fab-text-secondary mt-1">
                  These catalogues will be removed from your catalogue list. Your products are not deleted.
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-5">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                data-testid="delete-catalogue-cancel"
                className="px-4 py-2 rounded-lg border border-fab-primary-lighter text-fab-text-secondary text-[13px] font-semibold hover:bg-fab-elevated disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteCollections}
                disabled={deleting}
                data-testid="delete-catalogue-confirm-btn"
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-[13px] font-semibold hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                <Trash2 className="w-3.5 h-3.5" /> {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Seller-facing collection detail modal ────────────────────────────────
function CollectionDetailModal({ cid, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState('');
  useEffect(() => {
    setLoading(true);
    api.get(`/collections/${cid}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [cid]);
  if (loading) return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="collection-detail-modal">
      <div className="bg-white rounded-2xl p-6 text-fab-text-muted">Loading…</div>
    </div>
  );
  if (!data) return null;
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(data.view_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* ignore */ }
  };
  
const downloadAllImages = async () => {
  const products = (data?.products || []).filter(
    (p) => p.processed_image_url
  );

  if (!products.length || downloadingAll) return;

  setDownloadingAll(true);
  let completed = 0;
  setDownloadProgress(`0 of ${products.length} images downloaded`);

  try {
    for (const product of products) {
      const response = await fetch(product.processed_image_url);
      if (!response.ok) throw new Error('Image download failed');

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${product.product_code || 'image'}-${completed + 1}.jpg`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);

      completed++;
      setDownloadProgress(
        `${completed} of ${products.length} images downloaded`
      );

      await new Promise((resolve) => setTimeout(resolve, 300));
    }
  } catch (error) {
    setDownloadProgress(
      `Downloaded ${completed} of ${products.length}. Some images could not be downloaded.`
    );
  } finally {
    setDownloadingAll(false);
  }
};
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-start justify-center sm:p-4 overflow-y-auto" onClick={onClose} data-testid="collection-detail-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-3xl max-h-[92vh] sm:max-h-[calc(100vh_-_2rem)] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-fab-primary-lighter flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="font-display font-bold text-fab-text text-[15px] sm:text-[16px] truncate" data-testid="collection-detail-name">{data.name}</div>
            <div className="text-[12px] text-fab-text-muted truncate">{data.product_count} product{data.product_count === 1 ? '' : 's'} · {data.created_at ? new Date(data.created_at).toLocaleDateString('en-IN', { dateStyle: 'medium' }) : ''}</div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-fab-elevated rounded-lg shrink-0" data-testid="collection-detail-close"><CheckCircle2 className="w-0 h-0" /><span aria-hidden="true">✕</span></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
          {data.cover_image_url && (
            <div className="rounded-xl overflow-hidden border border-fab-primary-lighter">
              <img src={data.cover_image_url} alt="Cover" className="w-full h-auto object-cover" data-testid="collection-detail-cover" />
            </div>
          )}
          {data.description && (
            <div className="text-[13px] text-fab-text whitespace-pre-wrap leading-relaxed" data-testid="collection-detail-description">
              {data.description}
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={copyLink}
              data-testid="copy-collection-url"
              className="btn-primary px-4 py-2 text-[13px] inline-flex items-center gap-2"
            >
              {copied ? 'Copied' : 'Copy Link'}
            </button>
            
<button
  type="button"
  onClick={downloadAllImages}
  disabled={downloadingAll || !(data.products || []).some(p => p.processed_image_url)}
  className="bg-green-700 hover:bg-green-800 disabled:opacity-60 text-white font-medium px-4 py-2 text-[13px] rounded-lg"
>
  {downloadingAll ? 'Downloading...' : 'Download All Images'}
</button>

            {/* Hidden URL — retained so buyer links keep working and the seller
                can copy them, but the "View Collection" affordance itself was
                removed per Feb 2026 spec. */}
            <input type="hidden" data-testid="collection-hidden-url" value={data.view_url} readOnly />
          </div>
          {downloadProgress && (
  <div className="text-[12px] text-fab-text-muted mt-2">
    {downloadProgress}
  </div>
)}
          <div>
            <div className="text-[12px] font-semibold text-fab-text mb-2">Products in this collection</div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2" data-testid="collection-detail-products">
              {(data.products || []).map((p) => (
                <div key={p.id} className="rounded-lg overflow-hidden border border-fab-primary-lighter" data-testid={`collection-product-${p.product_code}`}>
                  <div className="aspect-square bg-fab-elevated">
                    {p.processed_image_url && <img src={p.processed_image_url} alt={p.product_code} className="w-full h-full object-cover" loading="lazy" />}
                  </div>
                  <div className="px-1.5 py-1 text-[10px] font-mono text-fab-text-secondary truncate text-center">{p.product_code}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

